import { useChunkErrorHandler } from '@shared/hooks/useChunkErrorHandler.ts';
import { recoverFromChunkLoadError } from '@shared/utils/appUpdate.ts';
import { type ReactNode, useEffect } from 'react';

import { ChunkErrorBoundary } from './ChunkErrorBoundary.tsx';

interface AppWrapperProps {
  children: ReactNode;
}

/**
 * AppWrapper component that initializes global error handlers for chunk loading failures.
 * This wraps the entire app and ensures chunk errors are caught and handled properly.
 */
export const AppWrapper = ({ children }: AppWrapperProps) => {
  // Initialize chunk error handling
  useChunkErrorHandler();

  // Also initialize service worker controller listener for updates
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleServiceWorkerMessage = (event: MessageEvent) => {
        if (event.data?.type === 'SW_UPDATED') {
          console.log('Service Worker updated to version:', event.data.version);
          // Optional: You could show a toast notification here
        }

        if (event.data?.type === 'BUILD_ASSET_MISSING') {
          console.warn('Build asset is missing, refreshing app shell:', event.data.url);
          recoverFromChunkLoadError();
        }
      };

      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

      // Check for updates periodically
      const updateInterval = setInterval(() => {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.update().catch(error => {
              console.error('Error checking for service worker updates:', error);
            });
          });
        });
      }, 60000); // Check every minute

      return () => {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
        clearInterval(updateInterval);
      };
    }
  }, []);

  return <ChunkErrorBoundary>{children}</ChunkErrorBoundary>;
};
